import React, { useState, useEffect } from 'react';
import PageMeta from '../components/PageMeta';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, CheckCircle2, Star, Users, Award, 
  Globe, TrendingUp, Target, Zap, BookOpen, Clock, Calendar,
  Phone, Mail, ChevronDown, ChevronUp, Sparkles,
  Shield, Rocket, GraduationCap, Brain, Bot,
  MessageCircle, Download, ArrowDown, Lightbulb, Eye,
  Compass, Heart, Loader2, Check, Timer
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { trackWhatsAppClick, trackFormSubmission } from '../utils/analytics';
import AdmissionEnrollment from '../components/AdmissionEnrollment';
import EnquireNowButton from '../components/EnquireNowButton';
import SiteFooter from '../components/SiteFooter';
import MobileCTABar from '../components/MobileCTABar';
import BrandLogo from '../components/BrandLogo';
import HeroPricingCard from '../components/HeroPricingCard';
import SiteHeader from '../components/SiteHeader';

const AIBootcampPage = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    studentName: '',
    parentName: '',
    phone: '',
    email: '',
    studentClass: '',
    city: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle anchor scroll on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  // Progress bar calculation - days until May 23, 2026
  useEffect(() => {
    const startDate = new Date('2026-03-01'); // Campaign start
    const endDate = new Date('2026-05-23T23:59:59'); // Deadline
    const now = new Date();
    
    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = now.getTime() - startDate.getTime();
    const progressPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
    
    setProgress(progressPercent);
  }, []);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.studentName,
          email: formData.email,
          phone: formData.phone,
          country: formData.city,
          qualification: formData.studentClass,
          experience: formData.parentName,
          message: `Student Class: ${formData.studentClass}, Parent: ${formData.parentName}`,
          course: 'AI Skills Bootcamp for School Students (Grades 8-12)',
          source: 'ai-bootcamp-page'
        })
      });
      
      if (response.ok) {
        trackFormSubmission('ai_bootcamp_application', {
          course: 'AI Skills Bootcamp',
          student_class: formData.studentClass
        });
        
        // Redirect to thank you page for conversion tracking
        navigate('/thank-you');
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
    setSubmitting(false);
  };

  const scrollToForm = () => {
    document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToCurriculum = () => {
    document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Sticky notes content
  const stickyNotes = [
    { text: "AI will reshape 80%+ careers in the next decade", rotation: -3, color: "bg-yellow-200" },
    { text: "Students who start early gain 3–5 years advantage", rotation: 2, color: "bg-blue-200" },
    { text: "Schools still don't teach real AI skills", rotation: -2, color: "bg-pink-200" },
    { text: "AI is becoming a basic skill like English", rotation: 4, color: "bg-green-200" },
    { text: "Freelancing opportunities start as early as Class 10–12", rotation: -4, color: "bg-orange-200" },
    { text: "AI improves thinking, not just technical skills", rotation: 1, color: "bg-purple-200" },
    { text: "Students become creators, not just consumers", rotation: -1, color: "bg-cyan-200" },
    { text: "Early exposure builds confidence in career decisions", rotation: 3, color: "bg-rose-200" },
    { text: "Global demand for AI-skilled youth is rising", rotation: -2, color: "bg-amber-200" },
    { text: "Understanding AI early reduces future confusion", rotation: 2, color: "bg-lime-200" }
  ];

  const faqs = [
    {
      question: "Is this bootcamp beginner-friendly?",
      answer: "Absolutely! This bootcamp is designed for students with zero prior AI knowledge. We start from the basics and gradually build up to real applications."
    },
    {
      question: "What age group is this for?",
      answer: "This bootcamp is specifically designed for students in grades 8th to 12th (ages 13-18). The curriculum is tailored to be engaging and age-appropriate."
    },
    {
      question: "Will there be recordings of the live classes?",
      answer: "Yes! All live sessions are recorded and made available within 24 hours. Students can revisit concepts anytime during the course duration."
    },
    {
      question: "What AI tools will be taught?",
      answer: "Students will learn ChatGPT, AI image generators, automation tools, prompt engineering, AI-powered content creation, and basics of no-code AI applications."
    },
    {
      question: "Is certification included?",
      answer: "Yes, upon successful completion of the bootcamp and final project, students receive an internationally recognized certificate that adds value to college applications."
    },
    {
      question: "What is the class schedule?",
      answer: "Classes are held on weekends (Saturday & Sunday) for 2 hours each, making it convenient for school students. Total 100 learning hours over 3 months."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <PageMeta
        title="AI Bootcamp Course | Job-Assured | AI School for All"
        description="Master AI tools, prompting, automation and workflows. Job-assured AI Bootcamp. New batches every month. Enroll now."
        path="/ai-bootcamp"
      />
      
      {/* STICKY TOP MARQUEE BAR */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 py-3">
        <div className="flex items-center justify-center gap-4 px-4">
          <div className="hidden md:flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-yellow-300 animate-pulse" />
            <span className="text-sm font-bold text-white">50% OFF Early Bird</span>
            <span className="text-white/60">|</span>
            <span className="text-sm text-white/90">Limited Seats</span>
            <span className="text-white/60">|</span>
            <span className="text-sm text-yellow-300 font-semibold">Advance Booking Closes New Batches Every Month!</span>
          </div>
          <Button 
            onClick={scrollToForm}
            className="bg-white text-purple-700 hover:bg-yellow-300 hover:text-purple-900 px-6 py-2 text-sm font-bold rounded-full transition-all"
          >
            Reserve Your Seat Now <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* GLOBAL SITE HEADER */}
      <SiteHeader ctaLabel="Enroll Now" ctaTo="/book-demo" />

      {/* SECTION 1: HERO SECTION - AI/FUTURISTIC THEME */}
      <section id="hero" className="relative min-h-screen flex items-center pt-8 overflow-hidden">
        {/* Futuristic AI Background */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1737505599159-5ffc1dcbc08f?w=1920&h=1080&fit=crop&q=80"
            alt="AI brain circuit visualization"
            className="w-full h-full object-cover" decoding="async" fetchPriority="high" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-purple-900/50"></div>
          {/* Neural network overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-violet-600/30 via-transparent to-transparent"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent"></div>
          {/* Animated grid */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'linear-gradient(rgba(139, 92, 246, 0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.4) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="container mx-auto max-w-7xl px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-violet-500/30 mb-8">
              <Brain className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-medium text-violet-300">Summer Break Special • Grades 8th–12th</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight text-white mb-6">
              Start Your{' '}
              <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
                AI Journey
              </span>
              {' '}Before You Even Finish School
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-6">
              During Your Summer Break, Build the Foundation of Your Future.
              <br />
              <span className="text-violet-300">Not Just Learning — Understanding What's Coming Next.</span>
            </p>

            {/* Support Content */}
            <p className="text-gray-400 text-lg mb-8 max-w-2xl">
              This 3-month AI bootcamp is not just a course. It's a guided journey where students and parents understand the future of careers, technology, and opportunities — and how to prepare for it early.
            </p>

            {/* Key Points */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              {[
                { icon: BookOpen, text: 'Learn from Basics to Real Applications' },
                { icon: Compass, text: 'Career Awareness + Skill Building' },
                { icon: Users, text: 'Live Mentorship + Guidance' },
                { icon: Award, text: 'International Certification' }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-violet-400" />
                  </div>
                  <span className="text-gray-300 text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={scrollToForm}
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white px-8 py-6 text-lg font-bold rounded-full shadow-2xl shadow-violet-500/30 transition-all hover:scale-105"
              >
                Enroll Now (50% OFF) <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <EnquireNowButton courseName="AI Bootcamp" size="md" label="Enquire Now" variant="outline" />
              <Button 
                onClick={scrollToCurriculum}
                variant="outline"
                className="border-2 border-violet-500/50 hover:border-violet-400 text-white px-6 py-6 text-lg rounded-full transition-all hover:bg-violet-500/10"
              >
                <Eye className="w-5 h-5 mr-2" /> Explore Curriculum
              </Button>
            </div>
            </div>
            <div className="lg:col-span-5">
              <HeroPricingCard
                courseName="AI Bootcamp for School Students"
                discountLabel="50% OFF · Summer Break"
                badge="3-Month Bootcamp · Grades 8–12"
                batchDate="New Batches Every Month"
                theme="violet"
                whatsappMessage="Hi, I want to know about the AI Skills Bootcamp for School Students"
              />
            </div>
          </div>
        </div>

        {/* Floating AI elements */}
        <div className="absolute bottom-20 right-10 w-64 h-64 opacity-30 hidden lg:block">
          <img 
            src="https://images.unsplash.com/photo-1764336312138-14a5368a6cd3?w=400&h=400&fit=crop&q=80"
            alt="Abstract AI visualization"
            className="w-full h-full object-contain" decoding="async" loading="lazy" />
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <ArrowDown className="w-6 h-6 text-violet-400" />
        </div>
      </section>

      {/* SECTION 2: WHY START AI EARLY - STICKY NOTES ON WHITEBOARD */}
      <section id="why-ai" className="py-24 px-6 relative overflow-hidden">
        {/* Whiteboard background with AI sketches feel */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-100 to-gray-50"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle, #6366f1 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900">
              Why Start{' '}
              <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">AI Early?</span>
            </h2>
          </div>

          {/* Sticky Notes Grid - Organic Layout */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {stickyNotes.map((note, i) => (
              <div 
                key={i}
                className={`${note.color} p-5 rounded-lg shadow-lg relative transition-all hover:scale-105 hover:shadow-xl cursor-default`}
                style={{ transform: `rotate(${note.rotation}deg)` }}
              >
                {/* Pin */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-red-500 rounded-full shadow-md border-2 border-red-600"></div>
                
                <p className="text-gray-800 font-medium text-sm leading-relaxed" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
                  "{note.text}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: INDUSTRY REALITY - AI LAB THEME */}
      <section id="industry-reality" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-purple-950/30 to-black"></div>
        {/* Neural network background */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1678845536613-5cf0ec5245cd?w=1920&h=1080&fit=crop&q=80"
            alt="Neural network visualization"
            className="w-full h-full object-cover" decoding="async" loading="lazy" />
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-8">
                AI Skills Are{' '}
                <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">No Longer Optional</span>
              </h2>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Degrees alone are no longer enough.
                <br />
                <span className="text-violet-300">The students who stand out are the ones who understand real-world tools and technologies.</span>
              </p>
              
              <div className="space-y-6 mb-10">
                {[
                  { icon: Lightbulb, text: "Extra skills = Career clarity", desc: "Understanding AI opens doors to multiple career paths" },
                  { icon: Compass, text: "Early exposure = Better decisions", desc: "Know what you want before choosing your stream" },
                  { icon: Shield, text: "Practical understanding = Confidence", desc: "Face the future with real skills, not just theory" }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-violet-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{item.text}</h3>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                onClick={scrollToForm}
                className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white px-8 py-4 text-lg font-bold rounded-full"
              >
                Build Your Foundation Today <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Right Visual - Robot/AI */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-violet-600/20 rounded-3xl blur-3xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1760629863094-5b1e8d1aae74?w=600&h=500&fit=crop&q=80"
                alt="Futuristic AI robot"
                className="relative rounded-3xl shadow-2xl border border-violet-500/20 w-full" decoding="async" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: TRANSFORMATION - ABSTRACT VISUAL */}
      <section id="transformation" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-black"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white">
              What Your Child Becomes{' '}
              <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">in 3 Months</span>
            </h2>
          </div>

          {/* Visual Split Transformation */}
          <div className="relative">
            <div className="grid md:grid-cols-2 min-h-[500px] rounded-3xl overflow-hidden shadow-2xl">
              {/* Before Side */}
              <div className="relative bg-gray-900 p-10 md:p-12 flex flex-col justify-center">
                <div className="absolute inset-0 opacity-30">
                  <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-950"></div>
                </div>
                <div className="relative z-10">
                  <span className="inline-block px-4 py-2 bg-gray-800 rounded-full text-gray-400 text-sm font-semibold mb-8">
                    BEFORE
                  </span>
                  <div className="space-y-6">
                    {[
                      "Confused about career direction",
                      "No practical exposure to technology",
                      "Only school-based theoretical learning",
                      "Uncertain about the future",
                      "Limited digital skills"
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-4 text-gray-400">
                        <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
                        <span className="text-lg">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* After Side */}
              <div className="relative bg-gradient-to-br from-violet-600 to-purple-700 p-10 md:p-12 flex flex-col justify-center">
                <div className="absolute inset-0 opacity-20">
                  <img 
                    src="https://images.unsplash.com/photo-1647356191320-d7a1f80ca777?w=800&h=600&fit=crop&q=80"
                    alt="Abstract AI pattern"
                    className="w-full h-full object-cover" decoding="async" loading="lazy" />
                </div>
                <div className="relative z-10">
                  <span className="inline-block px-4 py-2 bg-white/20 rounded-full text-white text-sm font-semibold mb-8">
                    AFTER 3 MONTHS
                  </span>
                  <div className="space-y-6">
                    {[
                      "Understands AI & future careers",
                      "Builds projects independently",
                      "Gains clarity & confidence",
                      "Has a clear direction",
                      "Ready for the digital future"
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-4 text-white">
                        <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-lg font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Center Arrow */}
            <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full shadow-2xl shadow-violet-500/50 items-center justify-center z-20">
              <ArrowRight className="w-10 h-10 text-white" />
            </div>
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={scrollToForm}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white px-8 py-4 text-lg font-bold rounded-full"
            >
              Start The Transformation <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 5: CURRICULUM - FUTURISTIC TIMELINE */}
      <section id="curriculum" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 to-black"></div>
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(139, 92, 246, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.5) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white">
              What You Will{' '}
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">Learn</span>
            </h2>
            <p className="text-gray-400 mt-4 text-lg">A 3-month journey from basics to building real projects</p>
          </div>

          {/* Vertical Timeline */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-violet-500 to-purple-500 rounded-full"></div>

            {/* Phase 1 */}
            <div className="relative flex flex-col md:flex-row items-start mb-16">
              <div className="md:w-1/2 md:pr-16 md:text-right mb-6 md:mb-0 pl-16 md:pl-0">
                <div className="inline-block">
                  <span className="inline-block px-4 py-2 bg-blue-500/20 text-blue-400 rounded-full text-sm font-bold mb-4 border border-blue-500/30">Month 1</span>
                  <h3 className="text-2xl font-bold text-white mb-3">Foundation</h3>
                  <p className="text-gray-400 mb-4">Understanding the AI landscape and building your thinking framework</p>
                  <ul className="space-y-2 text-gray-400">
                    <li>• Introduction to AI & Machine Learning</li>
                    <li>• Understanding AI Tools Landscape</li>
                    <li>• Prompt Engineering Basics</li>
                    <li>• Digital Thinking & Problem Solving</li>
                  </ul>
                </div>
              </div>
              <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/50">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <div className="md:w-1/2 md:pl-16"></div>
            </div>

            {/* Phase 2 */}
            <div className="relative flex flex-col md:flex-row-reverse items-start mb-16">
              <div className="md:w-1/2 md:pl-16 md:text-left mb-6 md:mb-0 pl-16 md:pl-16">
                <div className="inline-block">
                  <span className="inline-block px-4 py-2 bg-violet-500/20 text-violet-400 rounded-full text-sm font-bold mb-4 border border-violet-500/30">Month 2</span>
                  <h3 className="text-2xl font-bold text-white mb-3">Skill Building</h3>
                  <p className="text-gray-400 mb-4">Hands-on learning with real AI tools and applications</p>
                  <ul className="space-y-2 text-gray-400">
                    <li>• AI Content Creation</li>
                    <li>• Automation Tools Mastery</li>
                    <li>• Real-world Task Automation</li>
                    <li>• Building Mini Projects</li>
                  </ul>
                </div>
              </div>
              <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg shadow-violet-500/50">
                <Target className="w-4 h-4 text-white" />
              </div>
              <div className="md:w-1/2 md:pr-16"></div>
            </div>

            {/* Phase 3 */}
            <div className="relative flex flex-col md:flex-row items-start">
              <div className="md:w-1/2 md:pr-16 md:text-right mb-6 md:mb-0 pl-16 md:pl-0">
                <div className="inline-block">
                  <span className="inline-block px-4 py-2 bg-purple-500/20 text-purple-400 rounded-full text-sm font-bold mb-4 border border-purple-500/30">Month 3</span>
                  <h3 className="text-2xl font-bold text-white mb-3">Real Projects</h3>
                  <p className="text-gray-400 mb-4">Apply everything you've learned to create your portfolio</p>
                  <ul className="space-y-2 text-gray-400">
                    <li>• Build Complete AI Projects</li>
                    <li>• Portfolio Creation</li>
                    <li>• Presentation Skills</li>
                    <li>• Final Assessment & Certification</li>
                  </ul>
                </div>
              </div>
              <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg shadow-purple-500/50">
                <Rocket className="w-4 h-4 text-white" />
              </div>
              <div className="md:w-1/2 md:pl-16"></div>
            </div>
          </div>

          <div className="text-center mt-16">
            <Button 
              variant="outline"
              className="border-2 border-violet-500 text-violet-400 hover:bg-violet-500 hover:text-white px-8 py-4 text-lg rounded-full transition-all"
            >
              <Download className="w-5 h-5 mr-2" /> Download Full Curriculum
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 6: LEARNING EXPERIENCE - DIGITAL INTERFACE */}
      <section id="learning-experience" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-black"></div>
        {/* Abstract AI background */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1765046255517-412341954c4c?w=1920&h=1080&fit=crop&q=80"
            alt="Abstract data visualization"
            className="w-full h-full object-cover" decoding="async" loading="lazy" />
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                Not Just Classes.{' '}
                <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">Real Understanding.</span>
              </h2>
              
              <p className="text-xl text-gray-300 mb-10 leading-relaxed">
                We don't just teach — we guide. We help students see their future clearly, understand career possibilities, and build real confidence through hands-on learning.
              </p>
              
              <div className="space-y-8">
                {[
                  { icon: Users, title: "We guide students step-by-step", desc: "Every concept is explained with patience and clarity. No student left behind." },
                  { icon: Compass, title: "We help them understand careers", desc: "Real conversations about future opportunities, not just textbook knowledge." },
                  { icon: Heart, title: "We mentor, not just teach", desc: "Building relationships that go beyond the classroom." },
                  { icon: MessageCircle, title: "We answer real questions", desc: "Doubts about careers, technology, future — we address it all." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-5">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600/30 to-purple-600/30 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-7 h-7 text-violet-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Digital Interface Visual */}
            <div className="relative">
              <div className="absolute inset-0 bg-violet-600/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-950 rounded-3xl p-6 border border-violet-500/30 shadow-2xl">
                {/* Simulated learning interface */}
                <div className="bg-gray-800 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="ml-4 text-gray-400 text-sm">Live Session - AI Fundamentals</span>
                  </div>
                  <div className="bg-gradient-to-r from-violet-900/50 to-purple-900/50 rounded-lg h-32 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse mx-auto mb-2"></div>
                      <span className="text-white font-semibold">LIVE CLASS</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-800 rounded-lg p-3 text-center">
                    <Brain className="w-6 h-6 text-violet-400 mx-auto mb-1" />
                    <span className="text-xs text-gray-400">AI Tools</span>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 text-center">
                    <Target className="w-6 h-6 text-blue-400 mx-auto mb-1" />
                    <span className="text-xs text-gray-400">Projects</span>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 text-center">
                    <Award className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
                    <span className="text-xs text-gray-400">Certificate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: CERTIFICATION - AI THEMED */}
      <section id="certification" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-950/20 to-black"></div>
        
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            Earn a Certification That{' '}
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Reflects Real Learning</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-12 text-lg">
            Not just attendance-based. Our certification is project-based — proving you actually learned and built something real.
          </p>

          {/* Premium Certificate Mockup */}
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-3xl blur-3xl"></div>
            <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-2 shadow-2xl">
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-10 border border-amber-500/30">
                {/* Gold border */}
                <div className="border-2 border-amber-500/50 rounded-xl p-8 relative">
                  {/* Corner ornaments */}
                  <div className="absolute top-2 left-2 w-8 h-8 border-l-2 border-t-2 border-amber-500"></div>
                  <div className="absolute top-2 right-2 w-8 h-8 border-r-2 border-t-2 border-amber-500"></div>
                  <div className="absolute bottom-2 left-2 w-8 h-8 border-l-2 border-b-2 border-amber-500"></div>
                  <div className="absolute bottom-2 right-2 w-8 h-8 border-r-2 border-b-2 border-amber-500"></div>

                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30">
                      <Award className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  
                  <p className="text-amber-500 text-sm tracking-[0.3em] uppercase mb-2">Certificate of Achievement</p>
                  <h3 className="text-3xl font-serif text-white mb-1">AI Skills Bootcamp</h3>
                  <p className="text-amber-400/60 text-sm mb-6">For School Students (Grades 8-12)</p>
                  
                  <div className="w-48 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mb-6"></div>
                  
                  <p className="text-gray-400 text-sm mb-6">This certifies successful completion of<br/>the 100-hour AI Skills Bootcamp with project work</p>
                  
                  <div className="flex justify-center items-center gap-2 text-amber-400">
                    <Brain className="w-5 h-5" />
                    <span className="font-bold tracking-wider">AI SCHOOL FOR ALL</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-8 text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-violet-400" />
              <span>Project-Based Certification</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-violet-400" />
              <span>Internationally Recognized</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-violet-400" />
              <span>College Application Ready</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8: TIMING - CALM URGENCY (REDESIGNED) */}
      <section id="timing" className="py-32 px-6 relative overflow-hidden">
        {/* Dark premium background with AI elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-purple-950/40 to-indigo-950/30"></div>
        {/* Subtle glow effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-3xl"></div>
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-3xl"></div>
        
        {/* Neural network background pattern */}
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.unsplash.com/photo-1678845536613-5cf0ec5245cd?w=1920&h=1080&fit=crop&q=80"
            alt="Neural network"
            className="w-full h-full object-cover" decoding="async" loading="lazy" />
        </div>
        
        <div className="container mx-auto max-w-3xl relative z-10 text-center">
          {/* Small Tag */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/30 mb-8">
            <Timer className="w-4 h-4 text-violet-400" />
            <span className="text-violet-300 font-medium text-sm">Early Access Window</span>
          </div>

          {/* Main Heading */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            Right Time.{' '}
            <span className="bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent">Right Start.</span>
          </h2>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-gray-300 mb-6 max-w-2xl mx-auto leading-relaxed">
            The difference between confusion and clarity often comes down to when you begin.
          </p>

          {/* Supporting Line */}
          <p className="text-gray-400 mb-12 max-w-xl mx-auto">
            Advance bookings for this batch close on <span className="text-violet-300 font-semibold">New Batches Every Month</span>.
            <br />
            After this, the next intake will be limited and delayed.
          </p>

          {/* Progress Bar - Minimal & Elegant */}
          <div className="max-w-md mx-auto mb-12">
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>Enrollment Open</span>
              <span>Closing Soon</span>
            </div>
            <div className="h-2 bg-gray-800/50 rounded-full overflow-hidden backdrop-blur">
              <div 
                className="h-full bg-gradient-to-r from-violet-500 via-purple-500 to-blue-500 rounded-full transition-all duration-1000 relative"
                style={{ width: `${Math.min(progress, 85)}%` }}
              >
                {/* Animated glow */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full animate-pulse shadow-lg shadow-violet-500/50"></div>
              </div>
            </div>
            <p className="text-gray-500 text-sm mt-3">
              <span className="text-violet-400 font-medium">{Math.round(85 - progress)}%</span> of early bird seats remaining
            </p>
          </div>

          {/* CTA */}
          <Button 
            onClick={scrollToForm}
            className="bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white px-10 py-6 text-xl font-bold rounded-full shadow-2xl shadow-violet-500/20 transition-all hover:scale-105"
          >
            Secure Your Seat Today <ArrowRight className="w-6 h-6 ml-2" />
          </Button>

          {/* Below CTA note */}
          <p className="text-gray-500 mt-6 text-sm">
            Early Bird Offer <span className="text-green-400 font-semibold">(50% OFF)</span> valid till New Batches Every Month
          </p>
        </div>
      </section>

      {/* SECTION 9: PRICING - CLEAN TYPOGRAPHY */}
      <section id="pricing" className="py-24 px-6 relative">
        <div className="absolute inset-0 bg-black"></div>
        
        <div className="container mx-auto max-w-3xl relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            Investment in Their{' '}
            <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Future</span>
          </h2>
          
          <p className="text-gray-400 mb-12 text-lg">One-time payment for a lifetime of skills</p>

          {/* Clean Pricing Display */}
          <div className="mb-8">
            <p className="text-gray-500 text-2xl line-through mb-2">₹19,999</p>
            <div className="flex items-center justify-center gap-4">
              <span className="text-7xl md:text-8xl font-black bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">₹9,999</span>
            </div>
            <div className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-green-500/10 border border-green-500/30 rounded-full">
              <Sparkles className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-bold">50% OFF – Early Bird</span>
            </div>
          </div>

          <p className="text-gray-500 mb-8">Valid only for advance bookings till New Batches Every Month</p>

          <div className="flex flex-wrap justify-center gap-6 mb-10 text-gray-400">
            {[
              "100 Live Learning Hours",
              "International Certification",
              "Live Mentorship",
              "Project Portfolio",
              "Doubt Sessions",
              "Lifetime Access to Resources"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <Button 
            onClick={scrollToForm}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white px-12 py-6 text-xl font-bold rounded-full shadow-2xl shadow-green-500/30 transition-all hover:scale-105"
          >
            Book Now & Save 50%
          </Button>
        </div>
      </section>

      {/* SECTION 10: TESTIMONIALS - ABSTRACT AVATARS */}
      <section id="testimonials" className="py-24 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white">
              What Parents & Students{' '}
              <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "As a parent, I was worried about my son's career direction. This bootcamp helped him understand AI and gave him clarity about his future. The mentorship is exceptional.",
                name: "Parent from Delhi",
                role: "Son in Class 10",
                gradient: "from-violet-500 to-purple-500"
              },
              {
                quote: "I never thought I could build AI projects in school! The teachers explain everything so well. Now I help my classmates with their projects too. Feeling so confident!",
                name: "Student from Mumbai",
                role: "Class 11",
                gradient: "from-blue-500 to-cyan-500"
              },
              {
                quote: "Best investment for my daughter's future. She now talks about AI careers with such confidence. The practical approach and real projects made all the difference.",
                name: "Parent from Bangalore",
                role: "Daughter in Class 9",
                gradient: "from-purple-500 to-pink-500"
              }
            ].map((testimonial, i) => (
              <div key={i} className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-purple-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative bg-gray-900/50 backdrop-blur rounded-3xl p-8 border border-gray-800 group-hover:border-violet-500/30 transition-all h-full">
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-5 h-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 italic mb-8 leading-relaxed">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-4">
                    {/* Abstract avatar - no human face */}
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${testimonial.gradient} flex items-center justify-center`}>
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{testimonial.name}</p>
                      <p className="text-gray-500 text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11: FAQ */}
      <section id="faq" className="py-24 px-6 relative">
        <div className="absolute inset-0 bg-black"></div>
        
        <div className="container mx-auto max-w-3xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white">
              Frequently Asked{' '}
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div 
                key={i}
                className="border border-gray-800 rounded-2xl overflow-hidden hover:border-violet-500/30 transition-all"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left bg-gray-900/50 hover:bg-gray-900 transition-colors"
                >
                  <span className="text-white font-semibold pr-4">{faq.question}</span>
                  {openFaq === i ? (
                    <ChevronUp className="w-5 h-5 text-violet-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-6 py-5 bg-gray-950/50 border-t border-gray-800">
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 12: FINAL CTA */}
      <section id="final-cta" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-900 via-purple-900 to-indigo-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white/5 via-transparent to-transparent"></div>
        {/* AI glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-violet-500/20 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            Don't Let Them Start Late
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            The right start at the right time changes everything.
            <br />
            Give your child the foundation they need for the future.
          </p>

          <Button 
            onClick={scrollToForm}
            className="bg-white text-purple-700 hover:bg-yellow-300 hover:text-purple-900 px-12 py-8 text-2xl font-black rounded-full shadow-2xl transition-all hover:scale-105"
          >
            Book Your Seat Now (50% OFF) <ArrowRight className="w-7 h-7 ml-3" />
          </Button>

          <p className="text-white/60 mt-6">Limited seats • Batch starting soon</p>
        </div>
      </section>

      {/* SECTION 13: BOOKING FORM */}
      <section id="booking-form" className="py-24 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 to-black"></div>
        
        <div className="container mx-auto max-w-xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-white">
              Reserve Your{' '}
              <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">Seat</span>
            </h2>
            <p className="text-gray-400 mt-4">Fill the form and our team will contact you within 24 hours</p>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-purple-600/20 rounded-3xl blur-3xl"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-950 rounded-3xl p-8 border border-violet-500/30">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
                  <p className="text-gray-400 mb-4">Your seat reservation request has been submitted successfully.</p>
                  <p className="text-gray-400">Our team will contact you within 24 hours.</p>
                  <p className="text-sm text-gray-500 mt-6">Redirecting back...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-gray-300 mb-2 block font-medium">Student Name *</Label>
                      <Input
                        name="studentName"
                        value={formData.studentName}
                        onChange={handleFormChange}
                        placeholder="Enter student's name"
                        required
                        className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-violet-500"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-2 block font-medium">Parent Name *</Label>
                      <Input
                        name="parentName"
                        value={formData.parentName}
                        onChange={handleFormChange}
                        placeholder="Enter parent's name"
                        required
                        className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-violet-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-gray-300 mb-2 block font-medium">Phone Number *</Label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleFormChange}
                        placeholder="+91 98765 43210"
                        required
                        className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-violet-500"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-2 block font-medium">Email Address *</Label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        placeholder="parent@email.com"
                        required
                        className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-violet-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-gray-300 mb-2 block font-medium">Student's Class *</Label>
                      <select
                        name="studentClass"
                        value={formData.studentClass}
                        onChange={handleFormChange}
                        required
                        className="w-full bg-gray-800/50 border border-gray-700 text-white h-12 rounded-xl px-4 focus:border-violet-500 focus:outline-none"
                      >
                        <option value="">Select Class</option>
                        <option value="8th">8th Grade</option>
                        <option value="9th">9th Grade</option>
                        <option value="10th">10th Grade</option>
                        <option value="11th">11th Grade</option>
                        <option value="12th">12th Grade</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-2 block font-medium">City *</Label>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleFormChange}
                        placeholder="Enter your city"
                        required
                        className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-violet-500"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white py-6 text-lg font-bold rounded-xl transition-all"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Reserve My Seat <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-center text-gray-500 text-sm">
                    🔒 Your information is secure • Our team will contact you within 24 hours
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* DETAILED ENROLLMENT + ADMISSION PAYMENT */}
      <AdmissionEnrollment courseName="AI Bootcamp" theme="violet" source="ai-bootcamp-page" />
      <MobileCTABar courseName="AI Bootcamp" />

      <SiteFooter accent="violet" />

      {/* FLOATING CTA BUTTON */}
      <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${scrollY > 500 ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
        <Button 
          onClick={scrollToForm}
          className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white px-6 py-4 text-sm font-bold rounded-full shadow-2xl shadow-violet-500/30 flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" />
          Book Seat Now
        </Button>
      </div>

      {/* FLOATING WHATSAPP BUTTON */}
      <a
        href="https://wa.me/919891550666?text=Hi, I want to know about the AI Skills Bootcamp for School Students"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl shadow-green-500/30 transition-all hover:scale-110"
        onClick={() => trackWhatsAppClick('floating-button-bootcamp')}
        data-testid="whatsapp-float-bootcamp"
      >
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>
  );
};

export default AIBootcampPage;
